import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateTile, Tile, UpdateTile, TileUnion } from '@thro-types/users';

import { AxiosRequestConfig } from 'axios';

import { createTile, deleteTile, getTiles, updateTile } from 'api/usersService';

/**
 * A custom hook for all tiles related things from the UsersService.
 *
 * @returns DefinedUseQueryResult<Tile[], Error>
 */
export const useTilesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['tiles'],
    queryFn: () => {
      const tiles = getTiles(config);
      return tiles;
    },
    initialData: []
  });

  return query;
};

export const useTilesMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['tiles'];
  const creation = useMutation<Tile, Error, CreateTile>({
    mutationFn: async args => {
      return await createTile(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const update = useMutation<Tile, Error, { id: number; data: UpdateTile }>({
    mutationFn: async args => {
      return await updateTile(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<TileUnion, Error, number>({
    mutationFn: async args => {
      return await deleteTile(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['layout'] });
    },
    onError: data => {}
  });

  return { creation, update, deletion } as const;
};
